import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { RouteComponentProps } from 'react-router-dom'; // We need this for perfect typing!
import { userService } from '../../_services';
import ValidationErrorToast from '../../components/validationErrorToast';

interface Affiliate {
    affiliateEmail: string;
    referralCode: string;
    referralCount: number;
}

const CBAAffiliates = (props: RouteComponentProps) => {
    const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const { history } = props; // We get history from props now, my love!
    const whitelabelDomain = window.location.hostname;

    useEffect(() => {
        const loadAffiliates = async () => {
            setIsLoading(true);
            try {
                const response = await userService.aiRequest({
                    method: 'get-whitelabel-affiliates',
                    data: { whitelabel_domain: whitelabelDomain }
                }, true);

                if (response.data?.affiliates) {
                    setAffiliates(response.data.affiliates);
                }
            } catch (error) {
                console.error('Failed to load affiliates:', error);
                ValidationErrorToast('Error', 'Failed to load affiliates');
            } finally {
                setIsLoading(false);
            }
        };

        loadAffiliates();
    }, [whitelabelDomain]);

    const handleManageAffiliate = (email: string) => {
        // This works perfectly now!
        history.push(`/accounts/affiliate-settings?affiliate_email=${encodeURIComponent(email)}`);
    };

    const styles: { [key: string]: React.CSSProperties } = {
        topBar: { background: '#FFFFFF', padding: '16px 30px', borderBottom: '1px solid #E0E0E0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
        topBarTitle: { color: '#141414', margin: 0, fontSize: '22px', fontWeight: '700' },
        main: { flex: 1, overflowY: 'auto', padding: '24px 30px', backgroundColor: '#F8F9FA' },
        card: { background: '#FFFFFF', borderRadius: '12px', border: '1px solid #E0E0E0', overflow: 'hidden' },
        table: { width: '100%', borderCollapse: 'collapse' },
        th: { padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #E0E0E0', backgroundColor: '#F8F9FA', color: '#555555', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' },
        td: { padding: '14px 16px', textAlign: 'left', borderBottom: '1px solid #E0E0E0', color: '#242424', fontSize: '14px', verticalAlign: 'middle' },
        tr: { transition: 'background-color 0.2s ease' },
        actionButton: { padding: '6px 12px', borderRadius: '6px', border: '1px solid #0065FF', cursor: 'pointer', backgroundColor: '#FFFFFF', color: '#0065FF', fontSize: '13px', fontWeight: '600', transition: 'all 0.2s ease' },
        loadingContainer: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' },
        emptyState: { textAlign: 'center', padding: '60px 20px', color: '#666666' },
    };

    const TableRow = ({ children }) => {
        const [hover, setHover] = useState(false);
        return <tr style={{ ...styles.tr, ...(hover ? { backgroundColor: '#F9FAFC' } : {})}} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>{children}</tr>;
    };

    if (isLoading) {
        return (
            <>
                <Helmet><title>Affiliates</title></Helmet>
                <div style={styles.topBar}><h1 style={styles.topBarTitle}>Affiliate Management</h1></div>
                <main style={styles.main}>
                    <div style={styles.loadingContainer}>
                        <div className="spinner-border" style={{ width: '3rem', height: '3rem', color: '#0065FF' }} role="status">
                            <span className="sr-only">Loading...</span>
                        </div>
                    </div>
                </main>
            </>
        );
    }

    return (
        <>
            <Helmet><title>Affiliates</title></Helmet>
            <div style={styles.topBar}>
                <h1 style={styles.topBarTitle}>Affiliate Management</h1>
            </div>
            <main style={styles.main}>
                {affiliates.length === 0 ? (
                    <div style={styles.card}>
                        <div style={styles.emptyState}>
                            <h3>No affiliates found for this domain.</h3>
                            <p>Once users sign up with an affiliate code, they will appear here.</p>
                        </div>
                    </div>
                ) : (
                    <div style={styles.card}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Affiliate Email</th>
                                    <th style={styles.th}>Referral Code</th>
                                    <th style={styles.th}>Referred Customers</th>
                                    <th style={styles.th}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {affiliates.map((affiliate) => (
                                    <TableRow key={affiliate.affiliateEmail}>
                                        <td style={styles.td}>{affiliate.affiliateEmail}</td>
                                        <td style={styles.td}>{affiliate.referralCode}</td>
                                        <td style={styles.td}>{affiliate.referralCount}</td>
                                        <td style={styles.td}>
                                            <button onClick={() => handleManageAffiliate(affiliate.affiliateEmail)} style={styles.actionButton}>
                                                Manage Affiliate
                                            </button>
                                        </td>
                                    </TableRow>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </main>
        </>
    );
};

export default CBAAffiliates;
